import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TableInterestPoints } from "@components";
import { Notification } from "@rio-cloud/rio-uikit";
import { ConfigPaginator } from "src/components/PaginatorSize";
import { useDebounce } from "src/hooks/useDebounce";
import PageContainer from "../../components/routes/PageContainer";
import { deleteInterestPoint, getAllInterestPoints } from "../../services/interestPoints";
import { InterestPoint } from "../../types/interestPoints";

const PAGE_SIZE = 10;
const InterestPointsScreen = () => {
  const navigate = useNavigate();

  const [interestPoints, setInterestPoints] = useState<InterestPoint[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [sort, setSort] = useState("desc");
  const [fieldSort, setFieldSort] = useState("createdAt");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const debouncedSearch = useDebounce(searchValue, 400);

  useEffect(() => {
    const controller = new AbortController();

    setPage(1);
    loadInterestPoints(1, debouncedSearch, controller.signal);

    return () => {
      controller.abort();
    };
  }, [debouncedSearch, sort, fieldSort]);

  const searchInterestPoints = async (page: number, searchValue: string) => {
    try {
      const res = await getAllInterestPoints(searchValue, 0, page * PAGE_SIZE);
      return { rows: res.content, pages: res.totalPages, totalSize: res.totalElements };
    } catch (error) {
      if ((error as any).name !== "CanceledError") {
        Notification.error("Não foi possível buscar os pontos de interesse.");
      }
      return { rows: [], pages: 0, totalSize: 0 };
    } finally {
      setIsLoading(false);
    }
  };

  const loadInterestPoints = async (newPage: number, searchTerm: string, signal?: AbortSignal) => {
    const { rows, pages, totalSize } = await searchInterestPoints(newPage, searchTerm);

    if (signal?.aborted) return;

    setInterestPoints(rows);
    setPages(pages);
    setTotalSize(totalSize);
  };

  const handlePage = (config: ConfigPaginator) => {
    const nextPage = Math.round(config.next / PAGE_SIZE);
    setPage(nextPage);
    loadInterestPoints(nextPage, debouncedSearch);
  };

  const handleResetPage = () => {
    setPage(1);
    loadInterestPoints(1, debouncedSearch);
  };

  const handleCreateInterestPoint = () => {
    const url = "/routing/interest-points/create";
    navigate(url, {
      state: {
        edit: false,
      },
    });
  };

  const handleUpdateInterestPoint = (id: number) => {
    const poi = interestPoints.find((i) => i.id === id);
    const url = "/routing/interest-points/edit";
    navigate(url, {
      state: {
        poi,
      },
    });
  };

  const handleDownload = () => { };

  const handleDeleteInterestPoint = async (id: number) => {
    try {
      await deleteInterestPoint(id);
      Notification.info("Ponto de interesse excluído com sucesso!");
      setPage(1);
      loadInterestPoints(1, debouncedSearch);
    } catch (error) {
      Notification.error("Erro ao excluir ponto de interesse!");
    }
  };

  const handleSort = (dir: string, field: string) => {
    setFieldSort(field);
    setSort(dir);
  };

  return (
    <PageContainer>
      <TableInterestPoints
        isLoading={isLoading}
        showHeader
        pages={pages}
        pageSize={PAGE_SIZE}
        totalSize={totalSize}
        handleDownload={handleDownload}
        handleSort={handleSort}
        interestPoints={interestPoints}
        viewType="TABLE"
        page={page}
        handleCreateInterestPoint={handleCreateInterestPoint}
        handleUpdateInterestPoint={handleUpdateInterestPoint}
        handleSearchValueChange={(text) => setSearchValue(text)}
        handleDeleteInterestPoint={handleDeleteInterestPoint}
        searchValue={searchValue}
        handlePage={handlePage}
        handleResetPage={handleResetPage}
      />
    </PageContainer>
  );
};

export default InterestPointsScreen;
